import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {Customer} from '../types';
import {DelinquentBadge} from '../profile/DelinquentBadge';

// FNP-918: was `tables/columns.tsx` — a bare array where EVERY column was
// `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible — you
// cannot add `sortable: true` to a column that has nothing to sort on. Moved
// into a hook to match the platform shape (checklist §3z).
//
// ⚠️ `created` on a Stripe customer is a UNIX EPOCH IN SECONDS, not an
// AWSDateTime string. The old column multiplied by 1000 inline for display;
// that conversion now lives in `createdAtMillis` on the record (see
// useCustomers.ts) so DISPLAY and SORT agree. Sorting the raw seconds value
// would still order correctly, but the two would silently drift apart the
// moment either side changed.
export const useCustomersTableColumns = () => {

    const columns: DataTableColumn<Customer>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            width: '1fr',
            // Nullable on the Stripe API — a customer may exist with only an
            // email. Falls back to the id so the row is never a blank link.
            render: (item: Customer) => (
                <NavLink to={`/customers/${encodeURIComponent(item.id)}`}>
                    {item.name ?? item.id}
                </NavLink>
            ),
        },
        {
            accessor: 'email',
            sortable: true,
            title: I18n.get('Email'),
            width: '1fr',
            // Nullable fields render the em-dash placeholder, never blank or
            // `undefined` (§13). Nulls sort LAST in both directions — that is
            // sortRecords' rule, not something this column configures.
            render: (item: Customer) => item.email ?? '—',
        },
        {
            accessor: 'phone',
            sortable: true,
            title: I18n.get('Phone'),
            width: '1fr',
            render: (item: Customer) => item.phone ?? '—',
        },
        {
            accessor: 'delinquent',
            sortable: true,
            title: I18n.get('Delinquent'),
            width: '1fr',
            // Enum/boolean state renders as a badge, not a raw string (§13).
            render: (item: Customer) => <DelinquentBadge record={item}/>,
        },
        {
            // Sorts on the normalised millisecond value so the ordering matches
            // what the cell displays. `created` (seconds) is still the source.
            accessor: 'createdAtMillis',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: Customer) => formatTimestamp(item.createdAtMillis),
        },
    ];

    return {columns};
};
